import { useCallback } from 'react';
import { API_CONFIG } from '../config/api';

const processEvent = (eventLines) => {
  const eventTypeLine = eventLines.find((l) => l.startsWith("event: "));
  const dataLine = eventLines.find((l) => l.startsWith("data: "));

  if (!eventTypeLine || !dataLine) return { deltaText: "", isDone: false };

  const eventType = eventTypeLine.replace("event: ", "").trim();
  const dataStr = dataLine.replace("data: ", "").trim();

  if (eventType === "content_block_delta") {
    try {
      const cleanDataStr = dataStr.replace(/'/g, '"');
      const data = JSON.parse(cleanDataStr);
      
      if (data.type === "content_block_delta" && 
          data.delta && 
          data.delta.type === "text_delta") {
        return { deltaText: data.delta.text || "", isDone: false };
      }
    } catch (error) {
      console.error("Error parsing JSON:", error);
      return { deltaText: "", isDone: false };
    }
  } else if (eventType === "done") {
    return { deltaText: "", isDone: true };
  } else if (eventType === "response_start") {
    return { deltaText: "", isDone: false };
  }

  return { deltaText: "", isDone: false };
};

const deduplicateText = (text) => {
  const tokens = text.split(/(\s+)/);
  const cleanedTokens = [];
  let lastWord = null;
  for (const token of tokens) {
    if (/\s+/.test(token)) {
      cleanedTokens.push(token);
    } else {
      if (lastWord !== token) {
        cleanedTokens.push(token);
        lastWord = token;
      }
    }
  }
  return cleanedTokens.join("");
};

export const useChatApi = () => {
  const createUser = useCallback(async (name) => {
    try {
      const response = await fetch(`${API_CONFIG.USER_BASE_URL}${API_CONFIG.ENDPOINTS.CREATE_USER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });

      if (!response.ok) {
        throw new Error(`User creation failed with status: ${response.status}`);
      }

      const data = await response.json();
      if (data.status && data.data.userID) {
        return data.data.userID;
      }
      throw new Error('User ID not found in response');
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }, []);

  const getUser = useCallback(async (userId) => {
    try {
      const response = await fetch(`${API_CONFIG.USER_BASE_URL}${API_CONFIG.ENDPOINTS.GET_USER}/${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      
      if (!data.status) {
        throw new Error(data.message || 'User not found');
      }

      if (data.status && data.data && data.data.name) {
        return data.data.name;
      }
      throw new Error('User data not found in response');
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }, []);

  const getConversationHistory = useCallback(async (userId) => {
    try {
      const response = await fetch(`${API_CONFIG.CHAT_BASE_URL}${API_CONFIG.ENDPOINTS.GET_CONVERSATION}?user_id=${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch conversation history with status: ${response.status}`);
      }

      const data = await response.json();
      if (data.data && Array.isArray(data.data.messages)) {
        return data.data.messages.map(msg => ({
          sender: msg.sender === 'user' ? 'user' : 'bot',
          text: msg.message
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      return [];
    }
  }, []);

  const sendMessage = useCallback(async (input, userId, setMessages, setLoading, onStreamComplete) => {
    if (!input.trim()) return;
    setLoading(true);

    try {
      const response = await fetch(`${API_CONFIG.CHAT_BASE_URL}${API_CONFIG.ENDPOINTS.CONVERSATION}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: input,
          user_id: userId
        }),
      });

      if (!response.ok) {
        throw new Error(`Request failed with status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error("No response body from server");
      }

      setMessages((prev) => [...prev, { sender: "bot", text: "" }]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = "";
      let eventLines = [];
      let botTextBuffer = "";
      let pendingText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value);
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line === "") {
            const { deltaText, isDone } = processEvent(eventLines);
            eventLines = [];

            if (deltaText) {
              pendingText += deltaText;
              let spaceIndex;
              while ((spaceIndex = pendingText.indexOf(" ")) !== -1) {
                const word = pendingText.slice(0, spaceIndex + 1);
                pendingText = pendingText.slice(spaceIndex + 1);

                botTextBuffer += word;
                const deduplicatedBuffer = deduplicateText(botTextBuffer);

                setMessages((prev) => {
                  const updated = [...prev];
                  const lastBotIndex = updated.findLastIndex((m) => m.sender === "bot");
                  if (lastBotIndex !== -1) {
                    updated[lastBotIndex].text = deduplicatedBuffer;
                  }
                  return updated;
                });
              }
            }

            if (isDone) {
              if (pendingText) {
                botTextBuffer += pendingText;
                const deduplicatedBuffer = deduplicateText(botTextBuffer);
                setMessages((prev) => {
                  const updated = [...prev];
                  const lastBotIndex = updated.findLastIndex((m) => m.sender === "bot");
                  if (lastBotIndex !== -1) {
                    updated[lastBotIndex].text = deduplicatedBuffer;
                  }
                  return updated;
                });
              }
              break;
            }
          } else {
            eventLines.push(line);
          }
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "Something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
      if (onStreamComplete) {
        onStreamComplete();
      }
    }
  }, []);

  return { sendMessage, createUser, getUser, getConversationHistory };
};